import { hierarchy, tree, type HierarchyNode } from 'd3-hierarchy'
import type { SimulationLinkDatum, SimulationNodeDatum } from 'd3-force'
import type { MockEdge, MockNode, MockNodeType } from '../types'

/**
 * The D3 model behind the Knowledge Graph canvas (DSGN-63 radial spec).
 *
 * The page's graph is a tree (enterprise → site → documents, carried as
 * `parent` on each node) plus a few extra links (cross-sheet connections).
 * The tree drives depth, the radial seeding, collapse/expand and the tree
 * layout; the extra links only take part in the force simulation and the
 * drawing. Kept free of DOM and React so every rule here is unit-testable.
 */
export interface GraphNodeDatum extends SimulationNodeDatum {
  id: string
  label: string
  type: MockNodeType
  /** Circle radius, by type. */
  r: number
  /** Scale, animated on the inner group: 1 at rest, 1.3 selected, 0.8 context. */
  s: number
  depth: number
  parentId: string | null
  childIds: string[]
  /** Children are hidden (moved into `_children` in the spec's terms). */
  collapsed: boolean
  /** The label as drawn: long file names are middle-truncated; the full one is `label`. */
  displayLabel: string
  /** Estimated drawn width of `displayLabel` in px, so labels can keep clear of each other. */
  labelWidth: number
  /**
   * Extra distance from the parent for members of a large sibling group.
   * Real tenants hang a hundred documents off one site; one ring cannot hold
   * them, so siblings are dealt onto successive rings `RING_STEP` apart.
   */
  ringOffset: number
  x: number
  y: number
  vx: number
  vy: number
  fx?: number | null
  fy?: number | null
}

export interface GraphLinkDatum extends SimulationLinkDatum<GraphNodeDatum> {
  id: string
  source: GraphNodeDatum
  target: GraphNodeDatum
  /** A parent → child link (part of the tree) rather than an extra connection. */
  hierarchy: boolean
  label?: string
}

export interface GraphModel {
  nodes: GraphNodeDatum[]
  byId: Map<string, GraphNodeDatum>
  /** Extra, non-tree connections (both ends must be visible to draw). */
  extraLinks: Array<{ id: string; source: string; target: string; label?: string }>
  rootIds: string[]
}

/** Spec radii: company 22, site 18, refinery 15, equipment / documents 11. */
export const NODE_RADIUS: Record<MockNodeType, number> = {
  enterprise: 22,
  site: 18,
  operating_area: 15,
  operation_unit: 15,
  system: 15,
  functional_location: 11,
  components: 11,
  documents: 11,
  user_artifacts: 11,
}

/** Longest label drawn under a node; the full name stays in the hover card and the details panel. */
export const MAX_LABEL_CHARS = 26
export const truncateLabel = (label: string, max = MAX_LABEL_CHARS): string => {
  if (label.length <= max) return label
  const head = Math.ceil((max - 1) * 0.55)
  const tail = max - 1 - head
  return `${label.slice(0, head)}…${label.slice(label.length - tail)}`
}
/** Average glyph width as a fraction of the font size, for Inter / system sans. */
const GLYPH_EM = 0.58
export const labelFontSize = (depth: number): number => (depth <= 1 ? 13 : 11)
const estimateLabelWidth = (text: string, depth: number): number => text.length * labelFontSize(depth) * GLYPH_EM

/** Siblings per ring before another ring is opened, and the gap between rings. */
export const RING_CAPACITY = 14
export const RING_STEP = 90
/**
 * Which ring a sibling takes, dealt round-robin so every ring is a full
 * circle. Dealing them in runs (first 14 on ring one, next 14 on ring two)
 * made each ring an arc of the circle, and arcs on different radii shear
 * past each other as the graph contracts — the entrance visibly spun.
 */
export const ringOffsetFor = (siblingIndex: number, siblingCount: number): number => {
  const rings = Math.ceil(siblingCount / RING_CAPACITY)
  return rings <= 1 ? 0 : (siblingIndex % rings) * RING_STEP
}

export const buildModel = (nodes: MockNode[], edges: MockEdge[]): GraphModel => {
  const byId = new Map<string, GraphNodeDatum>()
  for (const n of nodes) {
    byId.set(n.id, {
      id: n.id,
      label: n.label,
      type: n.type,
      r: NODE_RADIUS[n.type],
      s: 1,
      depth: 0,
      parentId: n.parent && n.parent !== n.id ? n.parent : null,
      childIds: [],
      collapsed: false,
      displayLabel: truncateLabel(n.label),
      labelWidth: 0,
      ringOffset: 0,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
    })
  }
  // A parent that is not a node cannot hold children; treat such nodes as roots.
  for (const d of byId.values()) {
    if (d.parentId && !byId.has(d.parentId)) d.parentId = null
  }
  for (const d of byId.values()) {
    if (d.parentId) byId.get(d.parentId)?.childIds.push(d.id)
  }
  const rootIds = [...byId.values()].filter((d) => !d.parentId).map((d) => d.id)
  // Depth by walking down from the roots (a parent cycle cannot form: parentId
  // is a single pointer and roots have none).
  const stack = rootIds.map((id) => ({ id, depth: 0 }))
  const seen = new Set<string>()
  while (stack.length > 0) {
    const { id, depth } = stack.pop() as { id: string; depth: number }
    if (seen.has(id)) continue
    seen.add(id)
    const d = byId.get(id) as GraphNodeDatum
    d.depth = depth
    d.labelWidth = estimateLabelWidth(d.displayLabel, depth)
    d.childIds.forEach((c, i) => {
      const child = byId.get(c) as GraphNodeDatum
      child.ringOffset = ringOffsetFor(i, d.childIds.length)
      stack.push({ id: c, depth: depth + 1 })
    })
  }
  const treePairs = new Set([...byId.values()].filter((d) => d.parentId).map((d) => `${d.parentId}>${d.id}`))
  const extraLinks = edges
    .filter((e) => byId.has(e.source) && byId.has(e.target) && !treePairs.has(`${e.source}>${e.target}`))
    .map((e) => ({ id: e.id, source: e.source, target: e.target, label: e.label }))
  return { nodes: [...byId.values()], byId, extraLinks, rootIds }
}

/** Nodes with no collapsed ancestor, and the links among them. */
export const visibleGraph = (model: GraphModel): { nodes: GraphNodeDatum[]; links: GraphLinkDatum[] } => {
  const nodes: GraphNodeDatum[] = []
  const visible = new Set<string>()
  const walk = (id: string) => {
    const d = model.byId.get(id)
    if (!d) return
    nodes.push(d)
    visible.add(id)
    if (!d.collapsed) d.childIds.forEach(walk)
  }
  model.rootIds.forEach(walk)
  const links: GraphLinkDatum[] = []
  for (const d of nodes) {
    if (d.parentId && visible.has(d.parentId)) {
      links.push({ id: `${d.parentId}>${d.id}`, source: model.byId.get(d.parentId) as GraphNodeDatum, target: d, hierarchy: true })
    }
  }
  for (const e of model.extraLinks) {
    if (visible.has(e.source) && visible.has(e.target)) {
      links.push({ id: e.id, source: model.byId.get(e.source) as GraphNodeDatum, target: model.byId.get(e.target) as GraphNodeDatum, hierarchy: false, label: e.label })
    }
  }
  return { nodes, links }
}

/** d3.hierarchy over the VISIBLE tree, with a virtual root when there are several roots. */
const toHierarchy = (model: GraphModel): HierarchyNode<GraphNodeDatum | null> => {
  const childrenOf = (d: GraphNodeDatum | null): GraphNodeDatum[] =>
    d === null
      ? model.rootIds.map((id) => model.byId.get(id) as GraphNodeDatum)
      : d.collapsed
        ? []
        : d.childIds.map((id) => model.byId.get(id) as GraphNodeDatum)
  if (model.rootIds.length === 1) {
    return hierarchy<GraphNodeDatum | null>(model.byId.get(model.rootIds[0]) as GraphNodeDatum, childrenOf)
  }
  return hierarchy<GraphNodeDatum | null>(null, childrenOf)
}

/** Spec: ring radius per depth for the stretched entrance; deeper levels at depth × 95. */
export const ENTRANCE_RADII = [0, 140, 250, 345, 430, 505, 570]
export const entranceRadius = (depth: number): number => ENTRANCE_RADII[depth] ?? depth * 95

/**
 * Seed every visible node on a radial tree layout whose rings are LONGER than
 * the final link distances, so each branch starts stretched outward in a
 * nearly straight chain and the forces contract it into the settled graph.
 * This is the entrance animation; nothing tweens.
 *
 * Each node sits one ring gap beyond ITS PARENT along its own angle, rather
 * than on a ring around the page centre. For a bushy tree the two are the
 * same picture; for a real tenant — one site at the top of ring 1 with a
 * hundred documents spread round the whole of ring 2 — the centred version
 * pulled the far-side documents across the centre toward the site and the
 * whole star swung round as it contracted. Radiating from the parent, every
 * chain contracts straight along itself and nothing spins.
 */
export const seedRadialPositions = (model: GraphModel): void => {
  const root = toHierarchy(model)
  const virtual = root.data === null
  tree<GraphNodeDatum | null>()
    .size([2 * Math.PI, 1])
    .separation((a, b) => (a.parent === b.parent ? 1 : 1.6))(root)
  const primary = model.rootIds[0]
  root.each((h) => {
    if (!h.data) return
    // With several roots the first one owns the centre; the others (orphans
    // of the hierarchy) start on the first ring so nothing stacks at 0,0.
    const depth = virtual ? (h.depth === 1 && h.data.id !== primary ? 1 : h.depth - 1) : h.depth
    const angle = (h.x ?? 0) - Math.PI / 2
    const parent = h.parent?.data ?? null
    const gap = entranceRadius(depth) - (depth > 0 ? entranceRadius(depth - 1) : 0) + h.data.ringOffset
    const ox = parent ? parent.x : 0
    const oy = parent ? parent.y : 0
    h.data.x = ox + Math.cos(angle) * gap
    h.data.y = oy + Math.sin(angle) * gap
    h.data.vx = 0
    h.data.vy = 0
  })
  // The ROOT holds the page centre, pinned. It used to be the centre of
  // mass that was held there: everything was shifted so the mean position
  // sat on 0,0, and forceCenter kept it so. On the demo graph the two are the
  // same point. On a real tenant — one site with a hundred and eighty sheets
  // hanging off it — they are not: the mass sits around the site, so the
  // enterprise root was pushed off to one side and the graph looked
  // lopsided from the first frame. The reader expects the root in the
  // middle, as the design's reference frames draw it; pinning it there puts
  // it there for the entrance and for every settle after, with no camera
  // move. The seed already placed it at 0,0, so nothing shifts.
  const primaryNode = model.byId.get(primary)
  if (primaryNode) {
    primaryNode.fx = 0
    primaryNode.fy = 0
  }
}

export const TREE_NODE_SIZE: [number, number] = [150, 110]
/** A parent with more leaf children than this draws them as a grid beside it, not one endless row. */
export const TREE_GRID_MIN = 8
export const TREE_GRID_COL = 150
/** Row pitch of a leaf grid: a node, its label, and clear air before the next row (the design's 104px). */
export const TREE_GRID_ROW = 104
/** The design's grid is three columns wide; a group too large for that widens so it is not a mile tall. */
export const TREE_GRID_COLS = 3
/**
 * Clear space left between two neighbouring labels in the Tree View.
 *
 * Tree slots were a flat TREE_NODE_SIZE[0], so a label wider than 150px ran
 * into its neighbour's: a truncated 26-char name is about 166px at the depth
 * ≥2 size and 196px above it, and a row of document ids drew as one smear.
 * The label is centred under its node (`text-anchor: middle`), so neighbours
 * need half of each label between their centres, plus this gap.
 */
export const TREE_LABEL_GAP = 12
/** Extra air between two neighbouring subtrees, so one branch's block does not butt against the next. */
export const TREE_SUBTREE_GAP = 45

/** Columns of a leaf grid: three, until there are more than 27 leaves; then about √(n/3), keeping it roughly square. */
export const treeGridCols = (n: number): number =>
  Math.max(1, Math.min(n, Math.max(TREE_GRID_COLS, Math.ceil(Math.sqrt(n / TREE_GRID_COLS)))))

/**
 * A parent's sub-trees (children that have children of their own) are laid
 * side by side at most this many to a row; more wrap onto further rows below,
 * so a hub over sixty sheets stacks down the page as the design's Tree View
 * does instead of running one row tens of thousands of pixels wide.
 */
export const TREE_WRAP = 3
/** Air between the bottom of one row of sub-trees and the nodes of the next. */
export const TREE_ROW_GAP = 90
/** Space a label takes below its node's centre (radius + this). */
const LABEL_BELOW = 18

interface SubLayout {
  /** Positions relative to the sub-tree's root at 0,0. */
  pos: Map<string, { x: number; y: number }>
  left: number
  right: number
  bottom: number
}

/**
 * Top-down tree positions (the Tree View, DSGN-63 design frames).
 *
 * Each parent's LEAF children (more than TREE_GRID_MIN of them) are a grid
 * hanging down and to the right of it, `TREE_GRID_COLS` wide; its children
 * that have children of their own continue the tree below that grid, at most
 * TREE_WRAP to a row. A parent with only a few leaves keeps them on one row.
 *
 * Every sub-tree is laid out inside its own box (its node, its grid, its rows
 * of sub-trees), and boxes are packed side by side by their measured extents,
 * so no node or label can overlap another, whatever the tenant.
 */
export const treePositions = (model: GraphModel): Map<string, { x: number; y: number }> => {
  const kids = (d: GraphNodeDatum): GraphNodeDatum[] =>
    d.collapsed ? [] : d.childIds.map((id) => model.byId.get(id)).filter((c): c is GraphNodeDatum => !!c)
  const isLeaf = (d: GraphNodeDatum) => kids(d).length === 0
  const half = (d: GraphNodeDatum) => Math.max(TREE_NODE_SIZE[0], d.labelWidth + TREE_LABEL_GAP) / 2

  /** Lay `row` out from y0 down, in rows of `wrap`, each row centred on x = 0; merged into `into`. */
  const placeRows = (row: GraphNodeDatum[], y0: number, wrap: number, into: SubLayout) => {
    let y = y0
    for (let start = 0; start < row.length; start += wrap) {
      const chunk = row.slice(start, start + wrap)
      const subs = chunk.map(layoutSub)
      const xs: number[] = [0]
      for (let i = 1; i < chunk.length; i += 1) {
        const gap = isLeaf(chunk[i - 1]) && isLeaf(chunk[i]) ? 0 : TREE_SUBTREE_GAP
        xs.push(xs[i - 1] + subs[i - 1].right - subs[i].left + gap)
      }
      const shift = -(xs[0] + xs[xs.length - 1]) / 2
      let rowBottom = y
      subs.forEach((s, i) => {
        const ox = xs[i] + shift
        s.pos.forEach((p, id) => into.pos.set(id, { x: p.x + ox, y: p.y + y }))
        into.left = Math.min(into.left, ox + s.left)
        into.right = Math.max(into.right, ox + s.right)
        rowBottom = Math.max(rowBottom, y + s.bottom)
      })
      into.bottom = Math.max(into.bottom, rowBottom)
      y = rowBottom + TREE_ROW_GAP
    }
  }

  const layoutSub = (d: GraphNodeDatum): SubLayout => {
    const out: SubLayout = { pos: new Map([[d.id, { x: 0, y: 0 }]]), left: -half(d), right: half(d), bottom: d.r + LABEL_BELOW }
    const children = kids(d)
    const leaves = children.filter(isLeaf)
    let row = children
    let nextY = TREE_NODE_SIZE[1]
    if (leaves.length > TREE_GRID_MIN) {
      const cols = treeGridCols(leaves.length)
      const colW = Math.max(TREE_GRID_COL, leaves.reduce((w, c) => Math.max(w, c.labelWidth), 0) + TREE_LABEL_GAP)
      // The grid hangs to the parent's right, as the design draws it.
      const indent = (colW * 2) / 3
      const rows = Math.ceil(leaves.length / cols)
      leaves.forEach((leaf, i) => {
        out.pos.set(leaf.id, { x: indent + (i % cols) * colW, y: TREE_NODE_SIZE[1] + Math.floor(i / cols) * TREE_GRID_ROW })
      })
      const lastY = TREE_NODE_SIZE[1] + (rows - 1) * TREE_GRID_ROW
      out.left = Math.min(out.left, indent - colW / 2)
      out.right = Math.max(out.right, indent + (cols - 1) * colW + colW / 2)
      out.bottom = Math.max(out.bottom, lastY + leaves.reduce((r, c) => Math.max(r, c.r), 0) + LABEL_BELOW)
      nextY = lastY + TREE_NODE_SIZE[1]
      row = children.filter((c) => !isLeaf(c))
    }
    const wrap = row.some((c) => !isLeaf(c)) ? TREE_WRAP : Infinity
    placeRows(row, nextY, wrap, out)
    return out
  }

  // Several roots (the hierarchy's orphans) are one more row, from y = 0.
  const roots = model.rootIds.map((id) => model.byId.get(id)).filter((d): d is GraphNodeDatum => !!d)
  const all: SubLayout = { pos: new Map(), left: 0, right: 0, bottom: 0 }
  placeRows(roots, 0, roots.length === 1 ? 1 : Infinity, all)
  return all.pos
}

/** Collapse every node at `depth` and below that has children (e.g. 1 = "collapse to sites"). */
export const collapseToDepth = (model: GraphModel, depth: number): void => {
  for (const d of model.nodes) d.collapsed = d.childIds.length > 0 && d.depth >= depth
}

export const expandAll = (model: GraphModel): void => {
  for (const d of model.nodes) d.collapsed = false
}

/**
 * Toggle one node's children. On expand, every descendant starts at the
 * parent's position plus a sub-pixel random offset, so the forces fan them
 * out from there (and nothing divides by a zero distance).
 */
export const toggleChildren = (model: GraphModel, id: string): boolean => {
  const d = model.byId.get(id)
  if (!d || d.childIds.length === 0) return false
  d.collapsed = !d.collapsed
  if (!d.collapsed) {
    const place = (cid: string) => {
      const c = model.byId.get(cid)
      if (!c) return
      c.x = d.x + (Math.random() - 0.5)
      c.y = d.y + (Math.random() - 0.5)
      c.vx = 0
      c.vy = 0
      c.childIds.forEach(place)
    }
    d.childIds.forEach(place)
  }
  return true
}
